import React, { FunctionComponent, useMemo } from 'react';
import { Typography, Tooltip, useTheme, TTheme } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import { OpenAPI } from '@rbx/docsite-utilities/dist/browser';

export interface StabilityChipProps {
  stability: OpenAPI.OperationObject['x-roblox-stability'];
  tooltipPlacement?: React.ComponentProps<typeof Tooltip>['placement'];
}

interface StabilityConfig {
  labelKey: string;
  color: string;
  tooltipDescriptionKey: string;
}

export function getStabilityConfig(
  stability: NonNullable<OpenAPI.OperationObject['x-roblox-stability']>,
  theme: TTheme,
): StabilityConfig | null {
  switch (stability) {
    case 'STABLE':
      return {
        labelKey: 'Label.Stable',
        color: theme.palette.actionV2.primaryBrand.fill,
        tooltipDescriptionKey: 'Description.Stable',
      };
    case 'BETA':
      return {
        labelKey: 'Label.Beta',
        color: theme.palette.actionV2.active.fill,
        tooltipDescriptionKey: 'Description.Beta',
      };
    case 'EXPERIMENTAL':
      return {
        labelKey: 'Label.Experimental',
        color: theme.palette.actionV2.notice.fill,
        tooltipDescriptionKey: 'Description.Experimental',
      };
    default:
      return null;
  }
}

const StabilityChip: FunctionComponent<StabilityChipProps> = ({ stability, tooltipPlacement }) => {
  const { translate } = useTranslation();
  const theme = useTheme();
  const config = useMemo(() => {
    if (!stability) return null;
    return getStabilityConfig(stability, theme);
  }, [stability, theme]);

  if (!config) return null;

  return (
    <Tooltip
      title={translate(config.tooltipDescriptionKey)}
      {...(tooltipPlacement && { placement: tooltipPlacement })}>
      <Typography variant='body2' component='span' style={{ color: config.color }}>
        {translate(config.labelKey)}
      </Typography>
    </Tooltip>
  );
};

export default StabilityChip;
